import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import {
  AttendanceCAU,
  AttendanceCAUFile,
  AttendanceCauFileRequest
} from '../models/attendance-cau.model';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { MassiveResponse } from '../../massive-actions/models/massive.model';
import { ValidateAttendance } from '../models/validate-attendance.model';
import {
  UpdateAttendanceCAU,
  UpdateAttendanceCAUMassive,
} from '../models/update-attendance-cau.model';
import { AttendanceFilter } from '../models/attendance-filter.model';
import { StudentsAttendances } from '../models/student-attendance.model';
import { UpdateStudentAttendance, UpdateStudentAttendanceMassive } from '../models/update-student-attendance.model';
import { SchoolAttendanceMonitorResponse } from '../../monitor/models/school-attendance-monitor.model';
import { ServiceStudentAttendanceMonitorResponse, StudentsAttendancesValidateMonitorRequest } from '../../monitor/models/student-attendance-monitor.model';
import { UpdateAttendanceDayRequest } from '../../monitor/models/save-student-attendance-monitor.model';
import { MonitorFilter } from '../../monitor/models/monitor-filter.model';
import { SaveSchoolAttendanceMonitorRequest } from '../../monitor/models/save-school-attendance-monitor.model';
import { StudentAttendancePredictionResponse } from '../../monitor/models/student-attendance-prediction.model';
import { SaveStudentAttendancePredictionRequest } from '../../monitor/models/save-student-attendance-prediction.model';
import { ServiceAttendanceAdjustmentResponse } from '../../monitor/models/service-attendance-adjustment-response.model';
import { SaveServiceAttendanceAdjustmentRequest } from '../../monitor/models/save-service-attendance-adjustment.model';
import { AttendanceCAUFilter } from '../models/attendance-cau-filters.model';

@Injectable({
  providedIn: 'root',
})
export class AttendanceService {
  private apiUrl = `${environment.sgcApi}`;
  private attendanceRoute = 'attendance';
  private predictionRoute = 'prediction';

  constructor(
    private httpClient: HttpClient,
  ) {}

  getStudentAttendances(attendanceFilter: AttendanceFilter): Observable<StudentsAttendances> {
    let endpoint = `${this.apiUrl}${
      this.attendanceRoute
    }/student/exercise/${attendanceFilter.exerciseId}/year/${
      attendanceFilter.year
    }/month/${attendanceFilter.month}/serviceId/${
      attendanceFilter.serviceId
    }/schoolId/${attendanceFilter.schoolId}`;
    return this.httpClient
      .get<StudentsAttendances>(endpoint)
      .pipe(map((data) => data));
  }

  getAttendanceCau(attendanceCAUFileId: string): Observable<AttendanceCAU> {
    let endpoint = `${this.apiUrl}${this.attendanceRoute}/proposal/cau`;
    let json = JSON.stringify({"attendanceCAUFileId": attendanceCAUFileId});
    return this.httpClient.post<AttendanceCAU>(endpoint, json).pipe(
      map(data => data)
    );
  }

  updateAttendanceCAU(updateAttendanceCAU: UpdateAttendanceCAU, user: string): Observable<AttendanceCAU> {
    let endpoint = `${this.apiUrl}${this.attendanceRoute}/user/${user}/cau`;
    let json = JSON.stringify(updateAttendanceCAU);
    return this.httpClient
      .put<AttendanceCAU>(endpoint, json)
      .pipe(map((data) => data));
  }

  updateStudentAttendance(updateStudentAttendance: UpdateStudentAttendance,user: string): Observable<any> {
    let endpoint = `${this.apiUrl}${this.attendanceRoute}/user/${user}`;
    let json = JSON.stringify(updateStudentAttendance);
    return this.httpClient
      .put(endpoint, json);
  }

  updateAttendanceCAUMassive(updateAttendanceCAUMassive: UpdateAttendanceCAUMassive, user: string): Observable<MassiveResponse> {
    let endpoint = `${this.apiUrl}${this.attendanceRoute}/user/${user}/cau/massive`;
    let json = JSON.stringify(updateAttendanceCAUMassive);
    return this.httpClient
      .put<MassiveResponse>(endpoint, json)
      .pipe(map((data) => data));
  }

  updateStudentAttendanceMassive(updateStudentAttendanceMassive: UpdateStudentAttendanceMassive, user: string): Observable<MassiveResponse> {
    let endpoint = `${this.apiUrl}${this.attendanceRoute}/user/${user}/massive`;
    let json = JSON.stringify(updateStudentAttendanceMassive);
    return this.httpClient
      .put<MassiveResponse>(endpoint, json)
      .pipe(map((data) => data));
  }

  validateAttendanceSchool(validateAttendance: ValidateAttendance, user: string) {
    let endpoint = `${this.apiUrl}${this.attendanceRoute}/user/${user}/invoicing/school`;
    let json = JSON.stringify(validateAttendance);
    return this.httpClient
      .post(endpoint, json)
      .pipe(map((data) => data));
  }

  getSchoolAttendanceMonitor(monitorFilter: MonitorFilter) {
    const exerciseId = monitorFilter.exerciseId;
    const schoolId = monitorFilter.schoolId;
    const serviceId = monitorFilter.serviceId;
    const date = new Date(monitorFilter.filterDate);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    let endpoint = `${this.apiUrl}${this.attendanceRoute}/exerciseId/${exerciseId}/schoolId/${schoolId}/year/${year}/month/${month}/day/${day}/serviceId/${serviceId}/school`;
    return this.httpClient.get<SchoolAttendanceMonitorResponse>(endpoint)
    .pipe(map((data) => data));
  }

  getStudentAttendanceMonitor(monitorFilter: MonitorFilter): Observable<ServiceStudentAttendanceMonitorResponse> {
    const exerciseId = monitorFilter.exerciseId;
    const schoolId = monitorFilter.schoolId;
    const date = new Date(monitorFilter.filterDate);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const serviceId = monitorFilter.serviceId;

    let filter = this.getFilter(serviceId);
    let endpoint = `${this.apiUrl}${this.attendanceRoute}/exerciseId/${exerciseId}/school/${schoolId}/year/${year}/month/${month}/day/${day}/monitor/student${filter}`;
    return this.httpClient.get<ServiceStudentAttendanceMonitorResponse>(endpoint)
    .pipe(map((data) => data));
  }

  saveStudentAttendanceMonitorStatus(updateAttendanceDayRequest: UpdateAttendanceDayRequest, user: string) {
    let endpoint = `${this.apiUrl}${this.attendanceRoute}/user/${user}/day`;
    let json = JSON.stringify(updateAttendanceDayRequest);
    return this.httpClient
    .put(endpoint, json)
    .pipe(map((data) => data));
  }

  validateStudentAttendanceMonitorStatus(studentsAttendancesValidateMonitorRequest : StudentsAttendancesValidateMonitorRequest , user: string) {
    let endpoint = `${this.apiUrl}${this.attendanceRoute}/user/${user}/validate/massive`;
    let json = JSON.stringify(studentsAttendancesValidateMonitorRequest);

    return this.httpClient
    .put(endpoint, json)
    .pipe(map((data) => data));
  }

  saveSchoolAttendanceMonitor(saveSchoolAttendanceMonitorRequest: SaveSchoolAttendanceMonitorRequest, user: string) {
    let endpoint = `${this.apiUrl}${this.attendanceRoute}/user/${user}/monitor/school`;
    let json = JSON.stringify(saveSchoolAttendanceMonitorRequest);
    return this.httpClient
      .put(endpoint, json)
      .pipe(map((data) => data));
  }

  getServiceAttendanceAdjustment(adjustmentFilters: MonitorFilter) {
    const exerciseId = adjustmentFilters.exerciseId;
    const schoolId = adjustmentFilters.schoolId;
    const serviceId = adjustmentFilters.serviceId;
    const date = new Date(adjustmentFilters.filterDate);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    let endpoint = `${this.apiUrl}${this.attendanceRoute}/exerciseId/${exerciseId}/schoolId/${schoolId}/year/${year}/month/${month}/serviceId/${serviceId}`;
    return this.httpClient.get<ServiceAttendanceAdjustmentResponse>(endpoint)
    .pipe(map((data) => data));
  }

  saveServiceAttendanceAdjustment(saveServiceAttendanceAdjustmentRequest: SaveServiceAttendanceAdjustmentRequest, user: string) {
    let endpoint = `${this.apiUrl}${this.attendanceRoute}/user/${user}/monthclose/save`;
    let json = JSON.stringify(saveServiceAttendanceAdjustmentRequest);
    return this.httpClient
      .put(endpoint, json)
      .pipe(map((data) => data));
  }

  getStudentAttendancePrediction(monitorFilter: MonitorFilter): Observable<StudentAttendancePredictionResponse> {
    const schoolId = monitorFilter.schoolId;
    const date = new Date(monitorFilter.filterDate);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const serviceId = monitorFilter.serviceId;

    let endpoint = `${this.apiUrl}${this.attendanceRoute}/schoolId/${schoolId}/year/${year}/month/${month}/day/${day}/serviceId/${serviceId}/monitor/prediction`;
    return this.httpClient.get<StudentAttendancePredictionResponse>(endpoint)
    .pipe(map((data) => data));
  }

  saveStudentAttendancePrediction(saveStudentAttendancePredictionRequest: SaveStudentAttendancePredictionRequest, user: string) {
    let endpoint = `${this.apiUrl}${this.predictionRoute}/user/${user}/save`;
    let json = JSON.stringify(saveStudentAttendancePredictionRequest);
    return this.httpClient.put(endpoint, json).pipe(
      map((data) => data)
    );
  }

/* deg aqui
  getAttendanceFilesCau(attendanceCAUFilter: AttendanceCAUFilter): Observable<AttendanceCAUFile[]> {
    let endpoint = `${this.apiUrl}${this.attendanceRoute}/exercise/${attendanceCAUFilter.exerciseId}/cau${this.getFilters(attendanceCAUFilter)}`;
    return this.httpClient.get<AttendanceCAUFile[]>(endpoint)
      .pipe(map((data) => data));
  } */
  getAttendanceFilesCau(attendanceCAUFilter: AttendanceCAUFilter, user: string): Observable<AttendanceCAUFile[]> {
    let endpoint = `${this.apiUrl}${this.attendanceRoute}/exercise/${attendanceCAUFilter.exerciseId}/user/${user}/cau${this.getFilters(attendanceCAUFilter)}zoneId=1`;
    
    return this.httpClient.get<AttendanceCAUFile[]>(endpoint)
      .pipe(map((data) => data));
  }
      

  loadAttendanceCauFile(user: string, attendanceCauFileRequest: AttendanceCauFileRequest) {
    let endpoint = `${this.apiUrl}${this.attendanceRoute}/user/${user}/loadFile/cau`;
    let json = JSON.stringify(attendanceCauFileRequest);
    return this.httpClient.put(endpoint, json).pipe(
      map((data) => data)
    );
  }

  private getFilter(serviceId: number) {
    return serviceId ? `?filters=serviceId=${serviceId}` : '';
  }

  private getFilters(attendanceCAUFilter: AttendanceCAUFilter) {
    let urlFilter = '/?filters=';
    urlFilter += attendanceCAUFilter.year ? 'year=' + attendanceCAUFilter.year.toString() + ';' : '';
    urlFilter += attendanceCAUFilter.month ? 'month=' + attendanceCAUFilter.month.toString() + ';' : '';
    return urlFilter;
  }
}
